import { Component, Inject, Input, OnChanges, OnDestroy, Optional, SimpleChanges } from '@angular/core';
import {
  DwComponent,
  DwComponentType,
  DwExpressionService,
  DwMetaDataFormApi,
  DwMetaDataFormStateService,
  DwMetaDataService,
  DwMetaDataServiceToken,
  DwModalService,
  DwSectionBaseComponent,
  DwUiConfigRegistryService,
  DwUIMetaDataConfig,
  DwUIMetaDataConfigToken
} from '@devwareapps/devware-cap';
import { AppMetaDataItemNames, DocumentItemEntity } from '../../../../meta-data/app-meta-data.service';
import {
  FarAimProcessingCourse,
  FarAimLinkReprocessRequest,
  FarAimProcessingLesson,
  FarAimProcessingModule,
  FarAimProcessingStage,
  FarAimProcessingStageKey,
  FarAimProcessingStatusFilter,
  FarAimProcessingSummary
} from '../../models/far-aim-processing.model';
import { FarAimProcessingRepositoryService } from '../../services/far-aim-processing-repository.service';

@DwComponent({
  key: 'far-aim-processing',
  name: 'FAR/AIM Processing',
  componentType: DwComponentType.formSection,
  isGlobal: false,
  parentItemName: AppMetaDataItemNames.DocumentItem
})
@Component({
  selector: 'app-far-aim-processing',
  templateUrl: './far-aim-processing.component.html',
  styleUrls: ['./far-aim-processing.component.scss']
})
export class FarAimProcessingComponent extends DwSectionBaseComponent implements OnChanges, OnDestroy {
  private static readonly AI_PROCESSING_REFERENCE_TAB_NAME = 'aiProcessingReferenceDetail';

  @Input() documentItemId?: number;

  summary?: FarAimProcessingSummary;
  activeStageKey: FarAimProcessingStageKey = 'structured-import';
  activeStage?: FarAimProcessingStage;
  loading = false;
  loadAttempted = false;
  expandedCourseIds: { [courseId: number]: boolean } = {};
  expandedModuleIds: { [courseModuleId: number]: boolean } = {};
  displayedCourses: FarAimProcessingCourse[] = [];
  searchText = '';
  statusFilter: FarAimProcessingStatusFilter = 'all';
  showProblemsOnly = false;
  queueingReprocess = false;
  reprocessAvailable = true;
  reprocessMessage = '';
  reprocessError = '';
  readonly statusFilters: { Key: FarAimProcessingStatusFilter; Label: string }[] = [
    { Key: 'all', Label: 'All' },
    { Key: 'linked', Label: 'Linked' },
    { Key: 'active', Label: 'Queued / processing' },
    { Key: 'failed', Label: 'Failed' },
    { Key: 'not-linked', Label: 'Not linked' }
  ];

  private formApi?: DwMetaDataFormApi;
  private refreshTimerId?: number;

  constructor(
    dwExpressionService: DwExpressionService,
    protected dwUiConfigRegistryService: DwUiConfigRegistryService,
    @Inject(DwUIMetaDataConfigToken) uiMetaDataConfig: DwUIMetaDataConfig,
    @Inject(DwMetaDataServiceToken) dwMetaDataService: DwMetaDataService,
    @Optional() private dwFormStateService: DwMetaDataFormStateService,
    private farAimProcessingRepository: FarAimProcessingRepositoryService,
    private dwModalService: DwModalService
  ) {
    super(dwExpressionService, dwUiConfigRegistryService, uiMetaDataConfig, dwMetaDataService);
    this.formApi = this.dwFormStateService?.state?.formApi;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.documentItemId) {
      this.loadProcessing();
    }
  }

  setupComponentComplete(): void {
    this.runAfterFormDataLoaded(() => {
      if (!this.documentItemId && this.formApi) {
        this.documentItemId = this.formApi.getFormData<DocumentItemEntity>()?.DocumentItemId;
      }
      this.loadProcessing();
    });
  }

  ngOnDestroy(): void {
    this.stopAutoRefresh();
  }

  loadProcessing(): void {
    if (!this.documentItemId || this.loading) {
      return;
    }
    this.loading = true;
    this.loadAttempted = true;
    this.farAimProcessingRepository.getSummary(this.documentItemId).subscribe(summary => {
      this.summary = summary;
      this.reprocessAvailable = !['Queued', 'Processing', 'Waiting for AI', 'WaitingForAI']
        .includes(summary.ProcessingRunStatus || '');
      this.activeStage = summary.Stages.find(stage => stage.Key === this.activeStageKey) || summary.Stages[0];
      this.applyFilters();
      this.configureAutoRefresh();
      this.loading = false;
    }, () => {
      this.loading = false;
    });
  }

  updateSearch(searchText: string): void {
    this.searchText = (searchText || '').trim().toLowerCase();
    this.applyFilters();
  }

  selectStatusFilter(statusFilter: FarAimProcessingStatusFilter): void {
    this.statusFilter = statusFilter;
    this.applyFilters();
  }

  toggleProblemsOnly(): void {
    this.showProblemsOnly = !this.showProblemsOnly;
    this.applyFilters();
  }

  expandAll(): void {
    this.expandedCourseIds = {};
    this.expandedModuleIds = {};
    this.displayedCourses.forEach(course => {
      if (course.CourseId) { this.expandedCourseIds[course.CourseId] = true; }
      course.Modules.forEach(module => {
        if (module.CourseModuleId) { this.expandedModuleIds[module.CourseModuleId] = true; }
      });
    });
  }

  collapseAll(): void {
    this.expandedCourseIds = {};
    this.expandedModuleIds = {};
  }

  selectStage(stage: FarAimProcessingStage): void {
    this.activeStageKey = stage.Key;
    this.activeStage = stage;
  }

  toggleCourse(course: FarAimProcessingCourse): void {
    if (!course.CourseId) { return; }
    this.expandedCourseIds = { ...this.expandedCourseIds, [course.CourseId]: !this.expandedCourseIds[course.CourseId] };
  }

  toggleModule(module: FarAimProcessingModule): void {
    if (!module.CourseModuleId) { return; }
    this.expandedModuleIds = { ...this.expandedModuleIds, [module.CourseModuleId]: !this.expandedModuleIds[module.CourseModuleId] };
  }

  openAiProcessingReference(aiProcessingReferenceId?: number): void {
    if (!aiProcessingReferenceId) {
      return;
    }
    window.open(
      `/lms-admin/ai/advanced/ai-processing-references/edit/${aiProcessingReferenceId}`,
      FarAimProcessingComponent.AI_PROCESSING_REFERENCE_TAB_NAME
    );
  }

  queueReprocess(request: FarAimLinkReprocessRequest, scopeLabel: string, event?: Event): void {
    event?.stopPropagation();
    if (!this.documentItemId || this.queueingReprocess || !this.reprocessAvailable) {
      return;
    }

    this.dwModalService.showConfirmDialog({
      showConfirmDialog: true,
      title: 'Reprocess FAR/AIM lesson links',
      message: `Reprocess FAR/AIM lesson links for ${scopeLabel}? Active links in this scope will be re-evaluated.`
    }).subscribe(confirmed => {
      if (confirmed) {
        this.submitReprocess(request);
      }
    });
  }

  private submitReprocess(request: FarAimLinkReprocessRequest): void {
    if (!this.documentItemId || this.queueingReprocess || !this.reprocessAvailable) {
      return;
    }
    this.queueingReprocess = true;
    this.reprocessMessage = '';
    this.reprocessError = '';
    this.farAimProcessingRepository.queueLinkReprocess(this.documentItemId, request).subscribe(result => {
      this.queueingReprocess = false;
      this.reprocessAvailable = false;
      this.reprocessMessage = `Run #${result.DocumentLinkProcessingRunId} queued for ${result.QueuedLessonCount} lesson(s).`;
      this.loadProcessing();
    }, error => {
      this.queueingReprocess = false;
      this.reprocessError = error?.error?.detail || error?.error?.title
        || 'The FAR/AIM reprocessing request could not be queued.';
    });
  }

  queueDocumentReprocess(): void {
    this.queueReprocess({}, 'all linked courses');
  }

  queueCourseReprocess(course: FarAimProcessingCourse, event: Event): void {
    this.queueReprocess({ CourseId: course.CourseId }, course.CourseName || `course ${course.CourseId}`, event);
  }

  queueModuleReprocess(course: FarAimProcessingCourse, module: FarAimProcessingModule, event: Event): void {
    this.queueReprocess(
      { CourseId: course.CourseId, CourseModuleId: module.CourseModuleId },
      module.ModuleName || `module ${module.CourseModuleId}`,
      event
    );
  }

  queueLessonReprocess(lesson: FarAimProcessingLesson, event: Event): void {
    if (!lesson.CourseLessonId) {
      return;
    }
    this.queueReprocess(
      { CourseLessonId: lesson.CourseLessonId },
      lesson.LessonName || `lesson ${lesson.CourseLessonId}`,
      event
    );
  }

  trackStage(_: number, stage: FarAimProcessingStage): FarAimProcessingStageKey {
    return stage.Key;
  }

  trackCourse(_: number, course: FarAimProcessingCourse): number | string {
    return course.CourseId || course.CourseName || _;
  }

  trackModule(_: number, module: FarAimProcessingModule): number | string {
    return module.CourseModuleId || module.ModuleName || _;
  }

  trackLesson(_: number, lesson: { CourseLessonDocumentLinkId?: number; CourseLessonId?: number }): number {
    return lesson.CourseLessonDocumentLinkId || lesson.CourseLessonId || _;
  }

  trackStatusFilter(_: number, filter: { Key: FarAimProcessingStatusFilter }): FarAimProcessingStatusFilter {
    return filter.Key;
  }

  private applyFilters(): void {
    const courses = this.summary?.Courses || [];
    this.displayedCourses = courses.reduce((filteredCourses, course) => {
      const courseMatches = !this.searchText || course.SearchText.includes(this.searchText);
      const modules = course.Modules.reduce((filteredModules, module) => {
        const moduleMatches = courseMatches || !this.searchText || module.SearchText.includes(this.searchText);
        const lessons = module.Lessons.filter(lesson => {
          const matchesSearch = moduleMatches || lesson.SearchText.includes(this.searchText);
          return matchesSearch && this.matchesStatusFilter(lesson.LinkStatus) && (!this.showProblemsOnly || lesson.IsProblem);
        });
        if (!lessons.length) {
          return filteredModules;
        }
        filteredModules.push({ ...module, Lessons: lessons });
        return filteredModules;
      }, [] as FarAimProcessingModule[]);
      const hasDetailFilter = this.statusFilter !== 'all' || this.showProblemsOnly;
      if (modules.length || (!hasDetailFilter && courseMatches)) {
        filteredCourses.push({ ...course, Modules: modules });
      }
      return filteredCourses;
    }, [] as FarAimProcessingCourse[]);
  }

  private matchesStatusFilter(status?: string): boolean {
    if (this.statusFilter === 'all') { return true; }
    if (this.statusFilter === 'linked') { return status === 'Linked' || status === 'AI linked'; }
    if (this.statusFilter === 'not-linked') { return status === 'Not linked'; }
    if (this.statusFilter === 'active') { return status === 'Queued' || status === 'Processing'; }
    return (status || '').toLowerCase() === this.statusFilter;
  }

  private configureAutoRefresh(): void {
    this.stopAutoRefresh();
    if (!this.summary || (this.reprocessAvailable && !(this.summary.QueuedCount || this.summary.ProcessingCount))) {
      return;
    }
    this.refreshTimerId = window.setInterval(() => this.loadProcessing(), 15000);
  }

  private stopAutoRefresh(): void {
    if (this.refreshTimerId) {
      window.clearInterval(this.refreshTimerId);
      this.refreshTimerId = undefined;
    }
  }
}
